import { useEffect, useMemo, useState } from "react";
import { Printer, FileDown, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildPageSvg,
  CARD_SHEETS,
  cardSheetCellCount,
  getPaperMm,
  SHEET_PRESETS,
  sheetCellCount,
  type CardSheetId,
  type PageLayout,
  type PaperOrientation,
  type PaperSize,
  type SheetPresetId,
  type VcardCardData,
  type WifiCardData,
} from "@/lib/print-layout";
import { downloadPdfFromPageSvg, printPageSvg } from "@/lib/print";

export type LayoutKind = "single" | "grid" | "sheet" | "card";

export type CardTemplate =
  | { kind: "wifi-card"; data: WifiCardData; optionLabel: string }
  | { kind: "vcard-card"; data: VcardCardData; optionLabel: string };

export type PrintSetupDefaults = {
  /** Default barcode width in mm. Used for single + grid layouts. */
  widthMm: number;
  /** Default layout when the dialog opens. */
  layoutKind: LayoutKind;
  /** Grid defaults when layoutKind === "grid". */
  grid?: { cols: number; rows: number };
  /** Sheet preset when layoutKind === "sheet". */
  sheetPreset?: SheetPresetId;
  /** Default paper. Ignored when a sheet preset is active. */
  paper?: PaperSize;
  /** Default orientation. Ignored when a sheet preset is active. */
  orientation?: PaperOrientation;
};

export type PrintSetupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title for the print popup window and PDF metadata. */
  title: string;
  /** Filename stub (without extension) used for the PDF download. */
  filename: string;
  /** Source barcode SVG. */
  svgMarkup: string;
  /** Prefilled caption shown under each barcode. The user can edit. */
  defaultCaption: string;
  /** Per-generator smart defaults. */
  defaults: PrintSetupDefaults;
  /** Optional business-card template (Wi-Fi / vCard) the generator can offer
   *  alongside the standard label layouts. */
  cardTemplate?: CardTemplate;
};

const CARD_SHEET_IDS: CardSheetId[] = ["single", "averyL7414", "avery5371"];
const SHEET_PRESET_IDS = Object.keys(SHEET_PRESETS) as SheetPresetId[];

export function PrintSetupDialog({
  open,
  onOpenChange,
  title,
  filename,
  svgMarkup,
  defaultCaption,
  defaults,
  cardTemplate,
}: PrintSetupDialogProps) {
  const layoutOptions = useMemo<{ value: LayoutKind; label: string; desc: string }[]>(() => {
    const base: { value: LayoutKind; label: string; desc: string }[] = [
      { value: "single", label: "Single label", desc: "One barcode centred on the page." },
      { value: "grid", label: "Sheet (N × M)", desc: "Identical copies on a custom grid." },
      {
        value: "sheet",
        label: "Label sheet (Avery)",
        desc: "Pre-defined Avery 5160 / L7160 / etc.",
      },
    ];
    if (cardTemplate) {
      base.push({
        value: "card",
        label: cardTemplate.optionLabel,
        desc:
          cardTemplate.kind === "wifi-card"
            ? "Credit-card sized Wi-Fi card with QR + readable network info."
            : "Credit-card sized contact card with QR + name, phone, email.",
      });
    }
    return base;
  }, [cardTemplate]);

  const [layoutKind, setLayoutKind] = useState<LayoutKind>(defaults.layoutKind);
  const [widthMm, setWidthMm] = useState(defaults.widthMm);
  const [cols, setCols] = useState(defaults.grid?.cols ?? 3);
  const [rows, setRows] = useState(defaults.grid?.rows ?? 4);
  const [sheetPreset, setSheetPreset] = useState<SheetPresetId>(
    defaults.sheetPreset ?? "averyL7160",
  );
  const sheetCells = sheetCellCount(SHEET_PRESETS[sheetPreset]);
  const [sheetCopies, setSheetCopies] = useState(sheetCells);
  const [cardSheet, setCardSheet] = useState<CardSheetId>("single");
  const cardCells = cardSheetCellCount(CARD_SHEETS[cardSheet]);
  const [cardCopies, setCardCopies] = useState(cardCells);
  const [caption, setCaption] = useState(defaultCaption);
  const [paper, setPaper] = useState<PaperSize>(defaults.paper ?? "A4");
  const [orientation, setOrientation] = useState<PaperOrientation>(
    defaults.orientation ?? "portrait",
  );
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Re-prefill when reopened with a different barcode.
  useEffect(() => {
    if (open) setCaption(defaultCaption);
  }, [open, defaultCaption]);

  // When the preset / card sheet changes, clamp copies to the new cell count.
  useEffect(() => {
    setSheetCopies((prev) => Math.min(prev, sheetCells));
  }, [sheetCells]);
  useEffect(() => {
    setCardCopies(cardCells);
  }, [cardCells]);

  const layout = useMemo<PageLayout>(() => {
    if (layoutKind === "single") return { kind: "single", widthMm };
    if (layoutKind === "grid") return { kind: "grid", cols, rows, widthMm };
    if (layoutKind === "sheet") return { kind: "sheet", preset: sheetPreset, copies: sheetCopies };
    // card layout — only reachable when cardTemplate is set; fall back to
    // single label if somehow not.
    if (cardTemplate) {
      return cardTemplate.kind === "wifi-card"
        ? { kind: "wifi-card", sheet: cardSheet, copies: cardCopies, data: cardTemplate.data }
        : { kind: "vcard-card", sheet: cardSheet, copies: cardCopies, data: cardTemplate.data };
    }
    return { kind: "single", widthMm };
  }, [
    layoutKind,
    widthMm,
    cols,
    rows,
    sheetPreset,
    sheetCopies,
    cardSheet,
    cardCopies,
    cardTemplate,
  ]);

  const pageSvg = useMemo(() => {
    if (!svgMarkup) return "";
    return buildPageSvg({
      svgMarkup,
      caption,
      paper,
      orientation,
      layout,
    });
  }, [svgMarkup, caption, paper, orientation, layout]);

  // Sheet and card layouts dictate the paper / orientation themselves.
  const sheetActive = layoutKind === "sheet";
  const cardActive = layoutKind === "card";
  const effectivePaper = sheetActive
    ? SHEET_PRESETS[sheetPreset].paper
    : cardActive
      ? CARD_SHEETS[cardSheet].paper
      : paper;
  const effectiveOrient = sheetActive
    ? SHEET_PRESETS[sheetPreset].orientation
    : cardActive
      ? CARD_SHEETS[cardSheet].orientation
      : orientation;
  const paperMm = getPaperMm(effectivePaper, effectiveOrient);

  const onPrint = () => {
    if (!pageSvg) return;
    printPageSvg({ title, pageSvg });
  };

  const onDownloadPdf = async () => {
    if (!pageSvg) return;
    setPdfError(null);
    setPdfBusy(true);
    try {
      await downloadPdfFromPageSvg({ filename, pageSvg });
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : "Couldn't generate PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Print setup</DialogTitle>
          <DialogDescription>
            Choose layout, size and caption. Print at your browser&apos;s print dialog or download a
            vector PDF — both produce the same {paperMm.w.toFixed(0)}×{paperMm.h.toFixed(0)} mm
            output.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 overflow-y-auto px-6 py-4 md:grid-cols-[1fr_1.1fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Layout</Label>
              <Select
                value={layoutKind}
                onValueChange={(v) => setLayoutKind(v as typeof layoutKind)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {layoutOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex flex-col">
                        <span className="text-sm">{opt.label}</span>
                        <span className="text-xs text-muted-foreground">{opt.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {layoutKind === "grid" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="cols">Columns</Label>
                  <Input
                    id="cols"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    value={cols}
                    onChange={(e) => setCols(clampNum(e.target.value, 1, 20, 3))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rows">Rows</Label>
                  <Input
                    id="rows"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={50}
                    value={rows}
                    onChange={(e) => setRows(clampNum(e.target.value, 1, 50, 4))}
                  />
                </div>
              </div>
            )}

            {sheetActive && (
              <div className="space-y-2">
                <Label htmlFor="preset">Sheet preset</Label>
                <Select
                  value={sheetPreset}
                  onValueChange={(v) => setSheetPreset(v as SheetPresetId)}
                >
                  <SelectTrigger id="preset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SHEET_PRESET_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {SHEET_PRESETS[id].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="space-y-1">
                    <Label htmlFor="copies">Copies (max {sheetCells})</Label>
                    <Input
                      id="copies"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={sheetCells}
                      value={sheetCopies}
                      onChange={(e) =>
                        setSheetCopies(clampNum(e.target.value, 1, sheetCells, sheetCells))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Label size</Label>
                    <p className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                      {SHEET_PRESETS[sheetPreset].labelWMm} × {SHEET_PRESETS[sheetPreset].labelHMm}{" "}
                      mm
                    </p>
                  </div>
                </div>
              </div>
            )}

            {cardActive && (
              <div className="space-y-2">
                <Label htmlFor="card-sheet">Card sheet</Label>
                <Select value={cardSheet} onValueChange={(v) => setCardSheet(v as CardSheetId)}>
                  <SelectTrigger id="card-sheet">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CARD_SHEET_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {CARD_SHEETS[id].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="space-y-1">
                    <Label htmlFor="card-copies">Copies (max {cardCells})</Label>
                    <Input
                      id="card-copies"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={cardCells}
                      value={cardCopies}
                      onChange={(e) =>
                        setCardCopies(clampNum(e.target.value, 1, cardCells, cardCells))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Card size</Label>
                    <p className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                      {CARD_SHEETS[cardSheet].cardWMm} × {CARD_SHEETS[cardSheet].cardHMm} mm
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!sheetActive && !cardActive && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Barcode width</Label>
                  <span className="text-xs text-muted-foreground">{widthMm} mm</span>
                </div>
                <Slider
                  min={10}
                  max={Math.max(20, Math.floor(paperMm.w - 20))}
                  step={1}
                  value={[widthMm]}
                  onValueChange={(v) => setWidthMm(v[0])}
                />
              </div>
            )}

            {!cardActive && (
              <div className="space-y-2">
                <Label htmlFor="caption">Caption (under each barcode)</Label>
                <Input
                  id="caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Encoded value"
                  spellCheck={false}
                  autoComplete="off"
                  className="font-mono"
                />
              </div>
            )}

            {sheetActive || cardActive ? (
              <p className="text-xs text-muted-foreground">
                Paper and orientation are fixed by the {cardActive ? "card sheet" : "preset"} (
                {effectivePaper.toUpperCase()}, {effectiveOrient}).
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="paper">Paper</Label>
                  <Select value={paper} onValueChange={(v) => setPaper(v as PaperSize)}>
                    <SelectTrigger id="paper">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                      <SelectItem value="letter">US Letter (216 × 279 mm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="orient">Orientation</Label>
                  <Select
                    value={orientation}
                    onValueChange={(v) => setOrientation(v as PaperOrientation)}
                  >
                    <SelectTrigger id="orient">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {pdfError && (
              <p className="text-xs text-destructive" role="alert">
                {pdfError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Preview</Label>
            <div
              className="mx-auto w-full max-w-[min(100%,calc(40dvh*var(--aspect)))] overflow-hidden rounded-md border border-border bg-muted/40 md:max-w-none"
              style={{
                aspectRatio: `${paperMm.w} / ${paperMm.h}`,
                ["--aspect" as string]: `${paperMm.w / paperMm.h}`,
              }}
              aria-label="Page preview"
            >
              {pageSvg ? (
                <div
                  className="h-full w-full"
                  // svg-string-into-html-dangerously is acceptable here — the
                  // SVG is built by our own pure builder, no user HTML.
                  dangerouslySetInnerHTML={{ __html: stripXmlPrologAndForceFit(pageSvg) }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Generate a barcode first
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 border-t bg-background px-6 pb-6 pt-3 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onDownloadPdf} disabled={!pageSvg || pdfBusy}>
            {pdfBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FileDown className="h-4 w-4" aria-hidden />
            )}
            Download PDF
          </Button>
          <Button onClick={onPrint} disabled={!pageSvg}>
            <Printer className="h-4 w-4" aria-hidden />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function clampNum(raw: string, lo: number, hi: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

/** Override the SVG's mm width/height so it scales inside the preview box. */
function stripXmlPrologAndForceFit(svg: string): string {
  return svg
    .replace(/^<\?xml[^?]*\?>\s*/, "")
    .replace(/<svg([^>]*)\bwidth="[^"]+"/, "<svg$1")
    .replace(/<svg([^>]*)\bheight="[^"]+"/, "<svg$1")
    .replace(
      /<svg([^>]*)>/,
      '<svg$1 width="100%" height="100%" preserveAspectRatio="xMidYMid meet">',
    );
}
